import { useState } from "react";
import { BookingChangeRequestResponseDto } from "../../api/bookingChangeRequestApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  getInstrumentIcon,
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import {
  ArrowRight,
  Calendar,
  Check,
  Clock,
  Loader2,
  X,
  MessageSquare,
} from "lucide-react";
import { Link } from "react-router";
import { format } from "date-fns";
import { Trans, useLingui } from "@lingui/react/macro";
import ChangeRequestExpiry from "../booking/ChangeRequestExpiry.tsx";

interface PendingChangeRequestCardProps {
  changeRequest: BookingChangeRequestResponseDto;
  userId: string;
  onAccept: () => void;
  onReject: (reason?: string) => void;
  onSuggest: () => void;
  isProcessing: boolean;
}

export default function PendingChangeRequestCard({
  changeRequest,
  userId,
  onAccept,
  onReject,
  onSuggest,
  isProcessing,
}: PendingChangeRequestCardProps) {
  const { t } = useLingui();
  const { language, dateFnsLocale } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const InstrumentIcon = getInstrumentIcon(changeRequest.instrument);
  const instrumentLabel = getTranslations(
    supportedInstruments,
    changeRequest.instrument,
    language,
  );

  const formatTimeslot = (from: string, to: string): string => {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    return `${format(fromDate, "EEE MMM d, HH:mm", { locale: dateFnsLocale })} - ${format(toDate, "HH:mm", { locale: dateFnsLocale })}`;
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 shadow-[--shadow-card]">
      {/* Header: Move Request + instrument */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <InstrumentIcon className="h-5 w-5 shrink-0 text-blue-700" />
          <span className="truncate text-sm font-semibold text-blue-900">
            {instrumentLabel}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
          <Clock className="h-3 w-3" />
          <Trans>Move Request</Trans>
        </div>
      </div>

      {/* Requester info */}
      <div className="flex flex-col">
        <span className="text-xs text-blue-700">
          <Trans>Requested by</Trans>
        </span>
        <Link
          to={`/profile/${changeRequest.requestedBy}`}
          className="truncate text-sm font-medium text-blue-900 hover:underline"
        >
          {changeRequest.requestedByName}
        </Link>
      </div>

      {/* Message */}
      {changeRequest.message && (
        <div className="flex items-start gap-1.5 text-xs text-blue-700 italic">
          <MessageSquare className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="line-clamp-2">{changeRequest.message}</span>
        </div>
      )}

      {/* Timeslot changes */}
      <div className="flex flex-col gap-2">
        {changeRequest.timeslotChanges.map((change, index) => (
          <div
            key={index}
            className="flex flex-col gap-1 rounded-lg bg-blue-100/50 p-2"
          >
            <div className="flex items-center gap-1.5 text-[10px] text-blue-600">
              <Calendar className="h-3 w-3" />
              <span className="line-through opacity-70">
                {formatTimeslot(change.originalFrom, change.originalTo)}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-medium text-blue-900">
              <ArrowRight className="h-3 w-3 text-blue-500" />
              <span>
                {formatTimeslot(change.proposedFrom, change.proposedTo)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <ChangeRequestExpiry
        autoCancelAt={changeRequest.autoCancelAt}
        className="text-blue-700"
      />

      {/* Actions */}
      <div className="mt-1 flex flex-col gap-2">
        {showRejectForm ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3">
            <label className="mb-1.5 block text-sm font-medium text-red-800">
              <Trans>Reason for rejection (optional)</Trans>
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t`Let them know why you're declining...`}
              className="mb-2 w-full rounded-md border border-red-200 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-red-400 focus:ring-1 focus:ring-red-300 focus:outline-none"
              rows={2}
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  onReject(rejectReason.trim() || undefined);
                  setShowRejectForm(false);
                  setRejectReason("");
                }}
                disabled={isProcessing}
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {isProcessing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
                <Trans>Confirm Reject</Trans>
              </button>
              <button
                onClick={() => {
                  setShowRejectForm(false);
                  setRejectReason("");
                }}
                className="rounded-md px-3 py-1.5 text-sm text-red-700 transition-colors hover:bg-red-100"
              >
                <Trans>Cancel</Trans>
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <button
                onClick={onAccept}
                disabled={isProcessing}
                className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-green-700 disabled:opacity-50"
              >
                <Check className="h-4 w-4" />
                <Trans>Accept</Trans>
              </button>
              <button
                onClick={() => setShowRejectForm(true)}
                disabled={isProcessing}
                className="flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 shadow-sm transition-all hover:bg-red-50 disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                <Trans>Reject</Trans>
              </button>
            </div>
            {changeRequest.timeslotChanges.length === 1 && (
              <button
                onClick={onSuggest}
                disabled={isProcessing}
                className="flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-medium text-blue-600 shadow-sm transition-all hover:bg-blue-50 disabled:opacity-50"
              >
                <Calendar className="h-4 w-4" />
                <Trans>Suggest different time</Trans>
              </button>
            )}
          </>
        )}
      </div>

      <Link
        to={`/user/${userId}/bookings/${changeRequest.bookingId}`}
        className="text-center text-xs text-blue-600 hover:underline"
      >
        <Trans>View Booking Details</Trans>
      </Link>
    </div>
  );
}
